import { useCallback, useMemo } from 'react';
import { useHighlySensitivePropertyValues } from '../dataSensitivity/useHighlySensitivePropertyValues';
import { getOwnProperty } from '../utils/getOwnProperty';
import { getCachedAssociationValuesForField } from './utils';
const MISS = Symbol('MISS');
const DEFAULT_SENSITIVE_VALUES = {};
export function useGetPropertyFormValue({
  cachedPropertyValues,
  getSavedValue,
  objectId,
  objectTypeId,
  values
}) {
  const sensitiveValues = useHighlySensitivePropertyValues({
    objectTypeId,
    objectId,
    defaultValues: DEFAULT_SENSITIVE_VALUES
  });

  // We check the following sources in order to find the current value for a property:
  // 1. Draft values from the host app
  // 2. Sensitive values fetched internally
  // 3. Additional values from the host app via `getSavedValue`
  // 4. Cached values fetched internally
  const valueResolvers = useMemo(() => [name => getOwnProperty(values, name, MISS), name => getOwnProperty(sensitiveValues, name, MISS), name => {
    const savedValue = getSavedValue(name, objectId, objectTypeId);
    return savedValue === undefined ? MISS : savedValue;
  }, name => {
    const cachedProperty = getOwnProperty(cachedPropertyValues, name, MISS);
    return cachedProperty !== MISS && cachedProperty ? cachedProperty.value : MISS;
  }], [cachedPropertyValues, getSavedValue, objectId, objectTypeId, sensitiveValues, values]);
  return useCallback(propertyName => {
    for (const valueResolver of valueResolvers) {
      const value = valueResolver(propertyName);
      if (value !== MISS) {
        return value;
      }
    }
    return undefined;
  }, [valueResolvers]);
}
export const useGetAssociationFormValue = ({
  associationValues,
  cachedAssociationValues
}) => {
  return useCallback(fieldName => {
    const associationValue = associationValues[fieldName];
    const cachedObjectIds = getCachedAssociationValuesForField({
      fieldName,
      cachedAssociationValues
    });
    if (cachedObjectIds) {
      var _cachedObjectIds$valu, _cachedObjectIds$valu2;
      const combinedObjectIds = [...(((_cachedObjectIds$valu = cachedObjectIds.value) === null || _cachedObjectIds$valu === void 0 ? void 0 : _cachedObjectIds$valu.objectIds) || []), ...((associationValue === null || associationValue === void 0 ? void 0 : associationValue.objectIds) || [])];
      return {
        objectIds: combinedObjectIds.length ? combinedObjectIds : null,
        labelIdentifier: (associationValue === null || associationValue === void 0 ? void 0 : associationValue.labelIdentifier) || ((_cachedObjectIds$valu2 = cachedObjectIds.value) === null || _cachedObjectIds$valu2 === void 0 ? void 0 : _cachedObjectIds$valu2.labelIdentifier) || null
      };
    } else if (associationValue) {
      return associationValue;
    }
    return null;
  }, [associationValues, cachedAssociationValues]);
};